/**
 * Desktop Mega Menu image preview
 * Requirements:
 * - Links with: data-mega-menu-image="..."
 * - Preview container inside each submenu:
 *   <div data-mega-menu-preview><img data-mega-menu-preview-img></div>
 */
(function () {
  const LINK_ATTR = "[data-mega-menu-image]";
  const LINK_ANY = ".mega-menu__link, .menu-list__link, a" + LINK_ATTR;

  const PREVIEW = "[data-mega-menu-preview]";
  const PREVIEW_IMG = "[data-mega-menu-preview-img]";
  const DEFAULT_IMAGE_ATTR = "data-mega-menu-default-image";

  const isDesktop = () =>
    window.matchMedia && window.matchMedia("(min-width: 990px)").matches;

  function getSubmenuInnerFromTrigger(triggerLink) {
    const controls = triggerLink?.getAttribute?.("aria-controls");
    if (!controls) return null;
    const el = document.getElementById(controls);
    if (!el) return null;

    // ✅ include menu-list submenu wrappers
    return (
      el.closest(".menu-list__submenu-inner") ||
      el.closest(".menu-list__submenu") ||
      el.closest(".mega-menu__grid") ||
      el
    );
  }

  function getScopeFromAnyLink(a) {
    const byTrigger = getSubmenuInnerFromTrigger(a);
    if (byTrigger) return byTrigger;

    return (
      a.closest?.(".menu-list__submenu-inner") ||
      a.closest?.(".menu-list__submenu") ||
      a.closest?.(".mega-menu__grid") ||
      null
    );
  }

  function getParts(scope) {
    if (!scope) return { preview: null, img: null };
    return {
      preview: scope.querySelector(PREVIEW),
      img: scope.querySelector(PREVIEW_IMG),
    };
  }

  function defaultImageInPanel(scope) {
    const { preview, img } = getParts(scope);
    return (
      preview?.getAttribute?.(DEFAULT_IMAGE_ATTR) ||
      img?.getAttribute?.("src") ||
      null
    );
  }

  function show(scope, src) {
    const { preview, img } = getParts(scope);
    if (!preview || !img || !src) return;

    if (img.getAttribute("src") !== src) img.setAttribute("src", src);

    const unhide = () => {
      preview.hidden = false;
      window.dispatchEvent(new Event("resize"));
    };

    if (img.complete) {
      unhide();
    } else {
      img.addEventListener("load", unhide, { once: true });
      img.addEventListener(
        "error",
        () => {
          preview.hidden = true;
          window.dispatchEvent(new Event("resize"));
        },
        { once: true }
      );
    }
  }

  function resetToDefault(scope) {
    const src = defaultImageInPanel(scope);
    if (src) show(scope, src);
    else {
      const { preview } = getParts(scope);
      if (preview) preview.hidden = true;
    }
  }

  function ensureInitialWhenEnteringPanel(scope) {
    if (!scope) return;
    const { preview } = getParts(scope);
    if (!preview) return;
    if (preview.hidden === false) return;
    resetToDefault(scope);
  }

  function onHoverOrFocus(e) {
    if (!isDesktop()) return;

    // ✅ capture also menu-list links
    const a = e.target?.closest?.(LINK_ANY);
    if (!a) return;

    const scope = getScopeFromAnyLink(a);
    if (!scope) return;

    const src = a.getAttribute?.("data-mega-menu-image");
    if (src) show(scope, src);
    else resetToDefault(scope);
  }

  document.addEventListener("pointerover", onHoverOrFocus, true);
  document.addEventListener("focusin", onHoverOrFocus, true);

  document.addEventListener(
    "pointerenter",
    (e) => {
      if (!isDesktop()) return;
      const scope =
        e.target?.closest?.(".menu-list__submenu-inner") ||
        e.target?.closest?.(".menu-list__submenu");
      if (!scope) return;
      ensureInitialWhenEnteringPanel(scope);
    },
    true
  );

  document.addEventListener(
    "pointerleave",
    (e) => {
      if (!isDesktop()) return;
      const scope = e.target;
      if (!(scope instanceof HTMLElement)) return;
      if (!scope.matches?.(".menu-list__submenu-inner, .menu-list__submenu"))
        return;
      resetToDefault(scope);
    },
    true
  );

  const mo = new MutationObserver((mutations) => {
    if (!isDesktop()) return;

    for (const m of mutations) {
      if (m.type !== "attributes" || m.attributeName !== "aria-expanded")
        continue;

      const el = m.target;
      if (!(el instanceof HTMLElement)) continue;

      // ✅ also observe menu-list links
      if (!el.matches?.(".menu-list__link[aria-controls], .menu-list__link")) continue;

      const expanded = el.getAttribute("aria-expanded") === "true";
      const scope = getSubmenuInnerFromTrigger(el);
      if (!scope) continue;

      if (expanded) resetToDefault(scope);
      else resetToDefault(scope);
    }
  });

  mo.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-expanded"],
  });

  window.addEventListener("load", () => {
    if (!isDesktop()) return;
    document
      .querySelectorAll(".menu-list__link[aria-controls][aria-expanded='true']")
      .forEach((a) => {
        const scope = getSubmenuInnerFromTrigger(a);
        if (scope) resetToDefault(scope);
      });
  });
})();